import { useEffect } from "react";
import { useLocation, useHistory, useParams } from "react-router-dom";
import localizationManager from "../pages/localizationManager";
import { slugToLang, getLangPrefix, SUPPORTED_LANGS } from "../utils/langUrl";

const LANG_MAP = {
  ja: "ja",
  zh: "zh-TW", // Map 'zh' to 'zh-TW' if browser returns 'zh' or 'zh-CN'
  "zh-TW": "zh-TW",
  "zh-tw": "zh-TW", // Handle lowercase variant
  "zh-CN": "zh-TW", // Fallback for any Chinese to TC as per requirement
};

const LanguageHandler = () => {
  const history = useHistory();
  const location = useLocation();
  const { lang } = useParams(); // Expected to be :lang param from Router

  useEffect(() => {
    const handleLanguageLogic = async () => {
      const savedLang = localStorage.getItem("userLang");
      const currentPath = location.pathname;
      
      // Case 0: Redirect legacy /zh-TW/ or /zh/ URLs to canonical /zh-tw/
      if (lang && (lang === "zh-TW" || lang.toLowerCase() === "zh")) {
        const newPath = currentPath.replace(/^\/(zh-TW|zh)(\/|$)/i, "/zh-tw$2");
        if (newPath !== currentPath) {
          history.replace(newPath + location.search + location.hash);
          await localizationManager.setupLocalization("zh-TW");
          return;
        }
      }
      
      // Case 1: URL has a recognized language prefix
      const internalLang = slugToLang(lang);
      if (lang && internalLang && SUPPORTED_LANGS.includes(internalLang)) {
        if (localizationManager.currentLang !== internalLang) {
            await localizationManager.setupLocalization(internalLang);
        }
        return;
      }
      
      // Case 2: Legacy URL (No prefix)
      // Requirement: "Legacy URLs must remain valid... always show English... always canonicalize to /en/..."
      // BUT "Language Resolution Priority": 1. URL Prefix, 2. User Pref, 3. Browser, 4. English.
      // If we are here, URL Prefix is absent.
      
      // First Visit Detection (No saved preference)
      if (!savedLang) {
        const browserLang = navigator.language;
        let targetLang = "en"; // Default fallback

        // Detect
        const shortLang = browserLang.split("-")[0];
        if (LANG_MAP[browserLang]) {
            targetLang = LANG_MAP[browserLang];
        } else if (LANG_MAP[shortLang]) {
            // 'zh' -> 'zh-TW', 'ja' -> 'ja'
            targetLang = LANG_MAP[shortLang];
        } else if (shortLang === 'jp') { // rare but possible
            targetLang = 'ja';
        }
        
        // Redirect logic — only for non-English browsers
        if (targetLang !== "en") {
            // e.g. /product -> /ja/product
            // e.g. / -> /ja/
            const cleanPath = currentPath === "/" ? "" : currentPath;
            const prefix = getLangPrefix(targetLang);
            history.replace(`${prefix}${cleanPath}${location.search}${location.hash}`);
            await localizationManager.setupLocalization(targetLang);
            return;
        } else {
            // Browser assumes English — do NOT redirect legacy URLs to /en/
            localStorage.setItem("userLang", "en");
            await localizationManager.setupLocalization("en");
            return;
        }
      }

      // If user has saved preference but visits legacy URL
      // Legacy URLs always show English, no redirect.
      if (localizationManager.currentLang !== "en") {
         await localizationManager.setupLocalization("en");
      }
    };

    handleLanguageLogic();
  }, [lang, location.pathname, history, location.search, location.hash]);

  return null;
};

export default LanguageHandler;
